import { format, addMinutes } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import getFullDate from '@medflex/utils/getFullDate';
import SessionsScheduleForm, { Duration, Description } from '../SessionsScheduleForm';
import SessionsDetailModalSectionTitle from './SessionsDetailModalSectionTitle';

const SessionsDetailModalDataDisplay = ({
  scheduledAt,
  onSubmit,
  isEditable,
  disabled,
  snippets,
  title,
  description,
  scheduledEndedAt,
  hasPhoneParticipants,
}) => {
  const [composing, setComposing] = useState(false);
  const { t } = useTranslation('components');

  const handleClickEdit = () => setComposing(!composing);
  const handleSubmit = (
    { date, time, duration, description: descriptionNew, title: titleNew },
    event,
  ) => {
    const scheduledAtNew = getFullDate({ date, time });
    onSubmit(
      {
        scheduledAt: scheduledAtNew,
        title: titleNew,
        ...(descriptionNew && { description: descriptionNew }),
        ...(duration && { scheduledEndedAt: addMinutes(scheduledAtNew, duration) }),
      },
      event,
    );
  };

  const defaultDuration = useMemo(() => {
    if (scheduledEndedAt && scheduledAt) {
      return (new Date(scheduledEndedAt).getTime() - new Date(scheduledAt).getTime()) / 1000 / 60;
    }
    return null;
  }, [scheduledEndedAt, scheduledAt]);

  return (
    <section data-component="SessionsDetailModalDataDisplay">
      <SessionsDetailModalSectionTitle onEdit={handleClickEdit} isEditable={isEditable}>
        {t('Sessions.SessionsDetailModal.SessionsDetailModalDataDisplay.headline')}
      </SessionsDetailModalSectionTitle>
      {composing ? (
        <div className="p-1">
          <SessionsScheduleForm
            defaultDate={format(scheduledAt, 'DD.MM.YYYY')}
            defaultTime={format(scheduledAt, 'HH:mm')}
            onSubmit={handleSubmit}
            defaultTitle={title}
            onClose={handleClickEdit}
            className="bg-slate-100"
            isEditMode
            isDisabled={disabled}
          >
            {({ errors, ...props }) => (
              <>
                <Duration {...props} defaultValue={defaultDuration} error={errors.duration} />
                <Description
                  {...props}
                  error={errors.description}
                  snippets={snippets}
                  showWarning={hasPhoneParticipants}
                  defaultValue={description}
                />
              </>
            )}
          </SessionsScheduleForm>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <div className="font-semibold mb-2">
              {t('Sessions.SessionsDetailModal.SessionsDetailModalDataDisplay.title')}
            </div>
            <div>{title}</div>
          </div>
          <div />
          <div className="mb-4">
            <div className="mb-2 font-semibold">
              {t('Sessions.SessionsDetailModal.SessionsDetailModalDataDisplay.date')}
            </div>
            <div>{format(new Date(scheduledAt), 'DD.MM.YYYY')}</div>
          </div>
          <div className="mb-4">
            <div className="font-semibold mb-2">
              {t('Sessions.SessionsDetailModal.SessionsDetailModalDataDisplay.time')}
            </div>
            <div>
              {format(new Date(scheduledAt), 'HH:mm')}
              {scheduledEndedAt && ` - ${format(new Date(scheduledEndedAt), 'HH:mm')}`}
            </div>
          </div>
          {description && (
            <div>
              <div className="font-semibold mb-2">
                {t('Sessions.SessionsDetailModal.SessionsDetailModalDataDisplay.description')}
              </div>
              <div>{description}</div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

SessionsDetailModalDataDisplay.propTypes = {
  scheduledAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  scheduledEndedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onSubmit: PropTypes.func,
  isEditable: PropTypes.bool,
  disabled: PropTypes.bool,
  snippets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      id: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
  description: PropTypes.string,
  hasPhoneParticipants: PropTypes.bool,
};

SessionsDetailModalDataDisplay.defaultProps = {
  onSubmit: () => {},
  snippets: [],
};

export default SessionsDetailModalDataDisplay;
